import type { CesiumViewerType } from '@aus-platform/cesium';
import type { Entity } from 'cesium';
import { Cartesian3 } from 'cesium';
import { isNil } from 'lodash';
import { type Dispatch, type SetStateAction } from 'react';
import { getPromptSvg } from './ai-prompt-svg';
import { type PromptType } from './enums';

const PROMPT_SIZE = 32;

export class PolygonAIPromptRenderer {
  private readonly _viewer: CesiumViewerType;
  private readonly _entities: Entity[] = [];
  private readonly _setHasPrompts: Dispatch<SetStateAction<boolean>>;

  constructor(
    viewer: CesiumViewerType,
    setHasPrompts: Dispatch<SetStateAction<boolean>>,
  ) {
    this._viewer = viewer;
    this._setHasPrompts = setHasPrompts;
  }

  private _updatePromptState() {
    const hasPrompts = this._entities.length > 0;

    this._setHasPrompts((prev) => (prev === hasPrompts ? prev : hasPrompts));
  }

  addPrompt(
    longitude: number,
    latitude: number,
    altitude: number,
    promptType: PromptType,
  ): void {
    const entity = this._viewer.entities.add({
      position: Cartesian3.fromDegrees(longitude, latitude, altitude),
      billboard: {
        image: getPromptSvg(promptType),
        width: PROMPT_SIZE,
        height: PROMPT_SIZE,
        disableDepthTestDistance: Infinity,
      },
    });

    this._entities.push(entity);
    this._updatePromptState();
  }

  removeLastPrompt(): void {
    const entity = this._entities.pop();

    if (!isNil(entity)) {
      this._viewer.entities.remove(entity);
    }
    this._updatePromptState();
  }

  clearPrompts(): void {
    if (this._viewer.isDestroyed()) {
      return;
    }

    for (const entity of this._entities) {
      this._viewer.entities.remove(entity);
    }

    this._entities.length = 0;
    this._updatePromptState();
  }

  destroy(): void {
    this.clearPrompts();
  }
}
